import React, { useEffect, useState } from 'react'
import {
    FormTextFormField,
    FormTextAreaFormField,
    FormCheckBoxField,
    SubmitCancelButtons,
    FormDropdownField,
} from 'elements'
import { useForm, useFieldArray } from 'react-hook-form'
import { Form, Header, Segment, Message } from 'semantic-ui-react'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { CompanyInvoiceSettingService, ThemeRadioIds } from 'utility'
import { GetCompnayTheme, useNotification, useInvoiceSettings, useCurrenciesOptions } from 'hooks'
import { useAuth } from 'context'
// import { useTabletAndBelowMediaQuery } from 'layout'
import { Plus, Trash } from 'phosphor-react'

const QuestionSchema = yup.object().shape({
    LogoImagePath: yup.array().nullable(),
    HeaderImagePath: yup.array().nullable(),
    FooterImagePath: yup.array().nullable(),
    HeaderText: yup.string().nullable(),
    FooterText: yup.string().nullable(),
    // Invoice settings (all optional here, backend may apply defaults)
    DefaultCurrencyCode: yup.string().nullable(),
    DefaultPaymentTermsDays: yup
        .number()
        .transform(v => (Number.isNaN(v) ? undefined : v))
        .nullable(),
    DefaultTaxRateId: yup
        .number()
        .transform(v => (Number.isNaN(v) ? undefined : v))
        .nullable(),
    DefaultTaxRatePercent: yup
        .number()
        .transform(v => (Number.isNaN(v) ? undefined : v))
        .nullable(),
    NextInvoiceNumberInt: yup
        .number()
        .transform(v => (Number.isNaN(v) ? undefined : v))
        .nullable(),
    InvoiceNumberPrefix: yup.string().nullable(),
    InvoiceNumberFormat: yup.string().nullable(),
    DefaultFooterText: yup.string().nullable(),
    BillingAddress: yup.string().nullable(),
    BankDetails: yup.string().nullable(),
    AutoEmailOnCreate: yup.boolean().nullable(),
    AutoEmailToCustomerContact: yup.boolean().nullable(),
    TermsAndConditions: yup
        .array()
        .of(
            yup.object().shape({
                InvoiceSettingTermId: yup.number().nullable(),
                InvoiceSettingId: yup.number().nullable(),
                TermTitle: yup.string().required('Term title is required'),
                TermText: yup.string().required('Term text is required'),
                SortOrder: yup.number().default(0),
                IsActive: yup.boolean().default(true),
                CreatedOn: yup.string().nullable(),
                CreatedByUserId: yup.number().nullable(),
                ModifiedOn: yup.string().nullable(),
                ModifiedByUserId: yup.number().nullable(),
            }),
        )
        .default([]),
})

export const InvoiceScreen = () => {
    const { data } = GetCompnayTheme()
    const { data: invoiceData, refetch: refetchInvoice } = useInvoiceSettings()
    const [processing, setProcessing] = useState(false)
    const { data: currencyOptions } = useCurrenciesOptions()
    const { user } = useAuth()
    const { successToast } = useNotification()
    const {
        control,
        setValue,
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm({
        resolver: yupResolver(QuestionSchema),
        defaultValues: {
            // Defaults for invoice settings UI
            // DefaultCurrencyCode: 'USD',
            // DefaultPaymentTermsDays: 30,
            // DefaultTaxRateId: null,
            // DefaultTaxRatePercent: undefined,
            // NextInvoiceNumberInt: 1,
            // InvoiceNumberPrefix: 'INV-',
            // InvoiceNumberFormat: '{PREFIX}{YYYY}{NUMBER}',
            // DefaultFooterText: '',
            // BillingAddress: '',
            // BankDetails: '',
            // AutoEmailOnCreate: false,
            // AutoEmailToCustomerContact: true,
            TermsAndConditions: [
                {
                    InvoiceSettingTermId: 0,
                    InvoiceSettingId: 0,
                    TermTitle: '',
                    TermText: '',
                    SortOrder: 0,
                    IsActive: true,
                    CreatedOn: '',
                    CreatedByUserId: 0,
                    ModifiedOn: '',
                    ModifiedByUserId: 0,
                },
            ],
        },
    })

    // const LogoImagePath = watch('LogoImagePath')
    // const HeaderImagePath = watch('HeaderImagePath')
    // const FooterImagePath = watch('FooterImagePath')
    // const DefaultHeader = watch('DefaultHeader')
    // const DefaultFooter = watch('DefaultFooter')
    const {
        fields: termsFields,
        append,
        remove,
        // move,
    } = useFieldArray({
        control,
        name: 'TermsAndConditions',
    })

    // Update SortOrder when terms change
    useEffect(() => {
        termsFields.forEach((_, index) => {
            setValue(`TermsAndConditions.${index}.SortOrder`, index + 1)
        })
    }, [termsFields.length, setValue])

    // Enhanced appendTerm with proper SortOrder
    const appendTerm = item => {
        const newItem = {
            ...item,
            SortOrder: termsFields.length + 1,
            CreatedOn: new Date().toISOString(),
            CreatedByUserId: user?.userId || 0,
            ModifiedOn: new Date().toISOString(),
            ModifiedByUserId: user?.userId || 0,
        }
        append(newItem)
    }

    // Enhanced removeTerm to maintain SortOrder
    const removeTerm = index => {
        remove(index)
        // No need to manually update SortOrder here as the useEffect will handle it
    }

    useEffect(() => {
        if (data?.ThemeId) {
            reset({
                ...data,
                DefaultHeader: data.DefaultHeader || ThemeRadioIds.Image,
                DefaultFooter: data.DefaultFooter || ThemeRadioIds.Image,
                HeaderText: data.HeaderText || '',
                FooterText: data.FooterText || '',
            })

            /* SET PREVIEW FILES */
            setValue('LogoImagePath', [
                { url: data.LogoImagePath, preview: true },
            ])

            setValue('HeaderImagePath', [
                { url: data.HeaderImagePath, preview: true },
            ])

            setValue('FooterImagePath', [
                { url: data.FooterImagePath, preview: true },
            ])
        } else {
            reset({
                DefaultHeader: ThemeRadioIds.Image,
                DefaultFooter: ThemeRadioIds.Image,
            })
        }
    }, [data])

    // Populate invoice settings once fetched
    useEffect(() => {
        if (invoiceData) {
            const dto = invoiceData || {}
            setValue('DefaultCurrencyCode', dto.DefaultCurrencyCode ?? 'USD')
            setValue(
                'DefaultPaymentTermsDays',
                dto.DefaultPaymentTermsDays ?? 30,
            )
            setValue('DefaultTaxRateId', dto.DefaultTaxRateId ?? null)
            setValue(
                'DefaultTaxRatePercent',
                dto.DefaultTaxRatePercent ?? undefined,
            )
            setValue('NextInvoiceNumberInt', dto.NextInvoiceNumberInt ?? 1)
            setValue('InvoiceNumberPrefix', dto.InvoiceNumberPrefix ?? 'INV-')
            setValue(
                'InvoiceNumberFormat',
                dto.InvoiceNumberFormat ?? '{PREFIX}{YYYY}{NUMBER}',
            )
            setValue('DefaultFooterText', dto.DefaultFooterText ?? '')
            setValue('BillingAddress', dto.BillingAddress ?? '')
            setValue('BankDetails', dto.BankDetails ?? '')
            setValue('AutoEmailOnCreate', dto.AutoEmailOnCreate ?? false)
            setValue(
                'AutoEmailToCustomerContact',
                dto.AutoEmailToCustomerContact ?? true,
            )
            setValue(
                'TermsAndConditions',
                // Sort by SortOrder before displaying and update SortOrder to match array index
                Array.isArray(dto.TermsAndConditions) &&
                    dto.TermsAndConditions.length > 0
                    ? dto.TermsAndConditions.sort(
                        (a, b) => (a.SortOrder || 0) - (b.SortOrder || 0),
                    ).map((term, index) => ({
                        ...term,
                        InvoiceSettingTermId: term.InvoiceSettingTermId || 0,
                        InvoiceSettingId: term.InvoiceSettingId || 0,
                        TermTitle: term.TermTitle || '',
                        TermText: term.TermText || '',
                        SortOrder: index + 1, // Ensure SortOrder matches the array index + 1
                        IsActive: term.IsActive !== false,
                        CreatedOn: term.CreatedOn || '',
                        CreatedByUserId: term.CreatedByUserId || 0,
                        ModifiedOn: term.ModifiedOn || '',
                        ModifiedByUserId: term.ModifiedByUserId || 0,
                    }))
                    : [
                        {
                            InvoiceSettingTermId: 0,
                            InvoiceSettingId: 0,
                            TermTitle: '',
                            TermText: '',
                            SortOrder: 1, // Start with 1 for the first item
                            IsActive: true,
                            CreatedOn: new Date().toISOString(),
                            CreatedByUserId: user?.userId || 0,
                            ModifiedOn: new Date().toISOString(),
                            ModifiedByUserId: user?.userId || 0,
                        },
                    ],
            )
        }
    }, [invoiceData, setValue])

    const submit = async formData => {
        setProcessing(true)
        try {
            const submittedData = { ...formData }

            // Prepare and save invoice settings
            const invoicePayload = {
                InvoiceSettingId: invoiceData?.InvoiceSettingId || 0,
                CompanyId: user.CompanyId,
                DefaultCurrencyCode: submittedData.DefaultCurrencyCode ?? 'USD',
                DefaultPaymentTermsDays:
                    Number(submittedData.DefaultPaymentTermsDays) || 0,
                DefaultTaxRateId: submittedData.DefaultTaxRateId ?? null,
                DefaultTaxRatePercent:
                    submittedData.DefaultTaxRatePercent !== undefined &&
                        submittedData.DefaultTaxRatePercent !== null
                        ? Number(submittedData.DefaultTaxRatePercent)
                        : null,
                NextInvoiceNumberInt:
                    Number(submittedData.NextInvoiceNumberInt) || 1,
                InvoiceNumberPrefix:
                    submittedData.InvoiceNumberPrefix ?? 'INV-',
                InvoiceNumberFormat:
                    submittedData.InvoiceNumberFormat ??
                    '{PREFIX}{YYYY}{NUMBER}',
                DefaultFooterText: submittedData.DefaultFooterText ?? '',
                BillingAddress: submittedData.BillingAddress ?? '',
                BankDetails: submittedData.BankDetails ?? '',
                AutoEmailOnCreate: !!submittedData.AutoEmailOnCreate,
                AutoEmailToCustomerContact:
                    !!submittedData.AutoEmailToCustomerContact,
                TermsAndConditions: Array.isArray(
                    submittedData.TermsAndConditions,
                )
                    ? submittedData.TermsAndConditions.filter(
                        term =>
                            term?.TermTitle?.trim() && term?.TermText?.trim(),
                    ).map((term, index) => ({
                        ...term,
                        InvoiceSettingTermId: term.InvoiceSettingTermId || 0,
                        InvoiceSettingId: term.InvoiceSettingId || 0,
                        TermTitle: term.TermTitle.trim(),
                        TermText: term.TermText.trim(),
                        SortOrder: index + 1, // Ensure 1-based index
                        IsActive: term.IsActive !== false,
                        ModifiedOn: new Date().toISOString(),
                        ModifiedByUserId: user?.userId || 0,
                        CreatedOn: term.CreatedOn || new Date().toISOString(),
                        CreatedByUserId:
                            term.CreatedByUserId || user?.userId || 0,
                    }))
                    : [],
            }
            console.log('invoicePayload', invoicePayload)
            await CompanyInvoiceSettingService.save(invoicePayload)
            successToast('Invoice settings saved')
            refetchInvoice()
        } catch (error) {
            console.log('Error:', error)
        } finally {
            setProcessing(false)
        }
    }

    return (
        <>
            <Form noValidate onSubmit={handleSubmit(submit)}>
                <Segment raised>
                    <Header as="h4">Invoice Settings</Header>
                    <Form.Group widths="equal">
                        <FormDropdownField
                            control={control}
                            label="Default Currency Code"
                            name="DefaultCurrencyCode"
                            placeholder="Select Currency"
                            options={currencyOptions}
                            errors={errors.DefaultCurrencyCode}
                        />
                        <FormTextFormField
                            control={control}
                            label="Default Payment Terms (Days)"
                            name="DefaultPaymentTermsDays"
                            type="number"
                            errors={errors.DefaultPaymentTermsDays}
                        />
                        <FormTextFormField
                            control={control}
                            label="Default Tax Rate (%)"
                            name="DefaultTaxRatePercent"
                            type="number"
                            errors={errors.DefaultTaxRatePercent}
                        />
                    </Form.Group>
                    <Form.Group widths="equal">
                        <FormTextAreaFormField
                            control={control}
                            label="Default Footer Text"
                            name="DefaultFooterText"
                            errors={errors.DefaultFooterText}
                        />
                        <FormTextAreaFormField
                            control={control}
                            label="Billing Address"
                            name="BillingAddress"
                            errors={errors.BillingAddress}
                        />
                    </Form.Group>
                    <FormTextAreaFormField
                        control={control}
                        label="Bank Details"
                        name="BankDetails"
                        errors={errors.BankDetails}
                    />
                    <div className="mt-6">
                        <div className="flex justify-between items-center mb-4">
                            <Header as="h4" className="m-0">
                                Terms & Conditions
                            </Header>
                            <div className="flex items-center gap-2">
                                <span className="text-sm text-gray-500">
                                    {termsFields.length} term
                                    {termsFields.length !== 1 ? 's' : ''} added
                                </span>
                                <button
                                    type="button"
                                    onClick={() =>
                                        appendTerm({
                                            InvoiceSettingTermId: 0,
                                            InvoiceSettingId: 0,
                                            TermTitle: '',
                                            TermText: '',
                                            SortOrder: termsFields.length,
                                            IsActive: true,
                                            CreatedOn: new Date().toISOString(),
                                            CreatedByUserId: user?.userId || 0,
                                            ModifiedOn:
                                                new Date().toISOString(),
                                            ModifiedByUserId: user?.userId || 0,
                                        })
                                    }
                                    className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-50 text-blue-600 rounded-md hover:bg-blue-100 text-sm transition-colors"
                                >
                                    <Plus size={16} />
                                    Add Term
                                </button>
                            </div>
                        </div>

                        {termsFields && termsFields.length > 0 ? (
                            <div className="space-y-2">
                                {termsFields
                                    .sort(
                                        (a, b) =>
                                            (a.SortOrder || 0) -
                                            (b.SortOrder || 0),
                                    )
                                    .map((field, index) => (
                                        <div
                                            key={field.id}
                                            className="border rounded p-3 relative group"
                                        >
                                            <div className="flex items-start gap-3">
                                                <div className="flex-1 min-w-0">
                                                    <FormTextFormField
                                                        control={control}
                                                        name={`TermsAndConditions.${index}.TermTitle`}
                                                        label="Term Title"
                                                        placeholder="Term title"
                                                        className="text-sm font-medium"
                                                        size="small"
                                                        errors={
                                                            errors
                                                                .TermsAndConditions?.[
                                                                index
                                                            ]?.TermTitle
                                                        }
                                                        required
                                                    />
                                                    <FormTextAreaFormField
                                                        control={control}
                                                        name={`TermsAndConditions.${index}.TermText`}
                                                        label="Term Details"
                                                        placeholder="Term details..."
                                                        className="text-sm"
                                                        rows={2}
                                                        errors={
                                                            errors
                                                                .TermsAndConditions?.[
                                                                index
                                                            ]?.TermText
                                                        }
                                                        required
                                                    />
                                                    <FormCheckBoxField
                                                        control={control}
                                                        name={`TermsAndConditions.${index}.IsActive`}
                                                        label="Active"
                                                        className="text-sm"
                                                    />
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        removeTerm(index)
                                                    }
                                                    className="absolute top-2 right-2 text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                                                    title="Remove term"
                                                >
                                                    <Trash size={16} />
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                            </div>
                        ) : (
                            <div className="text-center py-8 border-2 border-dashed rounded-lg">
                                <p className="text-gray-500 mb-3">
                                    No terms and conditions added yet.
                                </p>
                                <button
                                    type="button"
                                    onClick={() =>
                                        appendTerm({
                                            InvoiceSettingTermId: 0,
                                            InvoiceSettingId: 0,
                                            TermTitle: '',
                                            TermText: '',
                                            SortOrder: 0,
                                            IsActive: true,
                                            CreatedOn: new Date().toISOString(),
                                            CreatedByUserId: user?.userId || 0,
                                            ModifiedOn:
                                                new Date().toISOString(),
                                            ModifiedByUserId: user?.userId || 0,
                                        })
                                    }
                                    className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-50 text-blue-600 rounded-md hover:bg-blue-100 text-sm transition-colors"
                                >
                                    <Plus size={16} />
                                    Add Your First Term
                                </button>
                            </div>
                        )}

                        {termsFields.length > 1 && (
                            <p className="text-xs text-gray-500 mt-2">
                                Tip: Use the order numbers to arrange your
                                terms. Lower numbers appear first.
                            </p>
                        )}
                    </div>
                    <Form.Group widths="equal" className="mt-2">
                        <FormCheckBoxField
                            control={control}
                            name="AutoEmailOnCreate"
                            label="Auto-email invoice on create"
                        />
                        <FormCheckBoxField
                            control={control}
                            name="AutoEmailToCustomerContact"
                            label="Auto-email to customer contact"
                        />
                    </Form.Group>
                </Segment>

                {Object.keys(errors).length > 0 && (
                    <Message
                        error
                        header="There were some errors with your submission"
                        list={Object.values(errors).map(err => err.message)}
                    />
                )}

                <SubmitCancelButtons loading={processing} />
            </Form>
        </>
    )
}
